import { z } from 'zod';
import type { AsyncResult } from '../../types/common';
import type { ApiError, RequestConfig } from './client';
import { err, ok } from '../../utils/fp-utils';

const RegistrationResponseSchema = z.object({
  success: z.literal(true),
  status: z.literal('registered'),
  message: z.string(),
  email_hint: z.string(),
});

const RegistrationErrorSchema = z.object({
  success: z.literal(false),
  status: z.enum(['invalid', 'rate_limited', 'email_failed']).optional(),
  message: z.string().optional(),
  field_errors: z.record(z.string()).optional(),
});

export type RegistrationResponse = z.infer<typeof RegistrationResponseSchema>;

export type RegistrationRequest = Readonly<{
  firstName?: string;
  email: string;
  consent: boolean;
  company?: string;
}>;

export type RegistrationApiError = ApiError & Readonly<{
  fieldErrors?: Record<string, string>;
}>;

const getRegistrationUrls = (): ReadonlyArray<string> => {
  const urls = new Set<string>();

  if (typeof window !== 'undefined') {
    urls.add(`${window.location.origin.replace(/\/$/, '')}/api/registration`);

    if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
      urls.add('http://localhost:8787/api/registration');
    }
  }

  return Array.from(urls);
};

export const registrationApi = {
  register: async (
    data: RegistrationRequest,
    config?: RequestConfig
  ): AsyncResult<RegistrationResponse, RegistrationApiError> => {
    const payload = {
      first_name: data.firstName?.trim() || '',
      email: data.email.trim().toLowerCase(),
      consent: data.consent,
      company: data.company || '',
      source: 'app-registration',
    };

    let lastError: RegistrationApiError = {
      message: 'The registration service is unavailable. Please try again.',
    };

    for (const url of getRegistrationUrls()) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(config?.headers || {}),
          },
          body: JSON.stringify(payload),
          signal: config?.signal,
        });

        const text = await response.text();
        let body: unknown = {};

        try {
          body = text ? JSON.parse(text) : {};
        } catch {
          lastError = {
            message: 'The registration service returned an invalid response.',
            status: response.status,
          };
          continue;
        }

        if (!response.ok) {
          const parsedError = RegistrationErrorSchema.safeParse(body);
          lastError = {
            message: parsedError.success
              ? parsedError.data.message || 'Registration failed. Please try again.'
              : `Registration failed with status ${response.status}.`,
            status: response.status,
            fieldErrors: parsedError.success ? parsedError.data.field_errors : undefined,
          };

          if (response.status >= 400 && response.status < 500) {
            return err(lastError);
          }

          continue;
        }

        const parsed = RegistrationResponseSchema.safeParse(body);
        if (!parsed.success) {
          lastError = {
            message: 'The registration service returned an invalid response.',
            status: response.status,
          };
          continue;
        }

        return ok(parsed.data);
      } catch (error) {
        lastError = {
          message: error instanceof Error
            ? error.message
            : 'The registration service is unavailable. Please try again.',
        };
      }
    }

    return err(lastError);
  },
};

